/**
 * One notification per event in the browser (TRUS-668).
 *
 * With browser notifications on, one new message (or an incoming call) reaches
 * this browser twice: as a push, which public/service-worker.js shows, and
 * through the app's own polling (NotificationBell for messages, the ringing
 * list for calls). Shown as two independent notifications, that was two
 * banners and two sounds.
 *
 * The app does not suppress its own notification: the backend may skip or
 * delay a push (mute, quiet hours, a stale token, FCM down), and then the poll
 * is the only thing that tells the user. Instead both paths COLLAPSE:
 *
 *  - Same tag for the same event on both sides: `message-<message_id>` for a
 *    message, `call-<call_id>` for a call, `notification-<id>` for any other
 *    notification row (the backend uses the same ones on the push).
 *  - Both are shown through the service worker registration
 *    (`registration.showNotification`). A page's `new Notification(...)` and a
 *    worker's notification never replace each other, even with one tag.
 *  - `renotify: false` on the app side, and the worker drops renotify when the
 *    app already showed that tag: a replacement never buzzes again.
 *  - Before showing, the app skips an event a push already showed in this
 *    browser, two ways:
 *      1. the worker posts `weavein:notification-shown` to every open window
 *         when it shows a push; windows remember those keys for a while. This
 *         is what stops a push the user already DISMISSED from being shown
 *         again by the next poll (a dismissed notification is gone from
 *         getNotifications()).
 *      2. `registration.getNotifications()`: still on screen (also covers a
 *         window that opened after the push, so never got the message 1).
 *
 * No service worker registration (browser notifications never turned on, a
 * browser without service workers) or the desktop app (Tauri: native
 * notifications, no web push, so nothing to duplicate): callers keep their
 * previous path. This module returns 'unavailable' and changes nothing there.
 */
import { isSecondaryAppWindow } from '@/lib/desktopWindow';

/** Posted by the service worker to open windows after it shows a push. */
export const NOTIFICATION_SHOWN_TYPE = 'weavein:notification-shown';

const DEFAULT_ICON = '/favicon-192.png';
const DEFAULT_BADGE = '/favicon-64.png';
/** How long a push the worker showed still suppresses the same event here. */
export const SHOWN_BY_PUSH_TTL_MS = 30 * 60_000;
const SHOWN_MAX = 500;

export const messageTag = (messageId: number | string) => `message-${messageId}`;
export const callTag = (callId: number | string) => `call-${callId}`;
export const notificationRowTag = (notificationId: number | string) => `notification-${notificationId}`;

export const isTauriApp = (): boolean => typeof window !== 'undefined' && '__TAURI__' in window;

/** What else identifies the event a notification is about (besides its tag). */
export interface NotificationIdentity {
  message_id?: number | string | null;
  notification_id?: number | string | null;
  call_id?: number | string | null;
}

/** Every key the event is known by: its tag plus message-/notification-/call- aliases. */
export function notificationKeys(tag: string | null | undefined, ids: NotificationIdentity = {}): string[] {
  const keys = new Set<string>();
  if (tag) keys.add(String(tag));
  const present = (v: unknown) => v !== null && v !== undefined && String(v) !== '' && String(v) !== '0';
  if (present(ids.message_id)) keys.add(messageTag(String(ids.message_id)));
  if (present(ids.notification_id)) keys.add(notificationRowTag(String(ids.notification_id)));
  if (present(ids.call_id)) keys.add(callTag(String(ids.call_id)));
  return Array.from(keys);
}

// --------------------------------------------------------------------------- //
// what the service worker already showed in this browser
// --------------------------------------------------------------------------- //
const shownByPush = new Map<string, number>();

export function rememberShownByPush(keys: string[], now = Date.now()): void {
  for (const k of keys) {
    shownByPush.delete(k);
    shownByPush.set(k, now);
  }
  while (shownByPush.size > SHOWN_MAX) {
    const oldest = shownByPush.keys().next().value;
    if (oldest === undefined) break;
    shownByPush.delete(oldest);
  }
}

export function wasShownByPush(keys: string[], now = Date.now()): boolean {
  return keys.some((k) => {
    const at = shownByPush.get(k);
    if (at === undefined) return false;
    if (now - at > SHOWN_BY_PUSH_TTL_MS) {
      shownByPush.delete(k);
      return false;
    }
    return true;
  });
}

/** Handle a message from the service worker; true when it was a "shown" notice. */
export function handleServiceWorkerMessage(event: MessageEvent): boolean {
  const msg = event?.data as ({ type?: string; tag?: string } & NotificationIdentity) | null;
  if (!msg || msg.type !== NOTIFICATION_SHOWN_TYPE) return false;
  rememberShownByPush(notificationKeys(msg.tag, msg));
  return true;
}

let listening = false;

/** Start remembering what the worker shows (idempotent; no-op without service workers). */
export function listenForPushShown(): void {
  if (listening || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    listening = true;
  } catch {
    /* no service worker container */
  }
}

// --------------------------------------------------------------------------- //
// showing through the registration
// --------------------------------------------------------------------------- //
/** The active service worker registration this browser shows notifications with, or null. */
export async function notificationRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return null;
  if (isTauriApp()) return null;
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker) return null;
  if (!('Notification' in window) || Notification.permission !== 'granted') return null;
  try {
    // Not `serviceWorker.ready`: it never settles when nothing is registered.
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg || !reg.active || typeof reg.showNotification !== 'function') return null;
    return reg;
  } catch {
    return null;
  }
}

function sameId(a: unknown, b: unknown): boolean {
  return a !== null && a !== undefined && b !== null && b !== undefined && String(a) !== '' && String(a) === String(b);
}

/** A notification about this event is on screen (by tag, or one that names the same message / row / call). */
export async function isOnScreen(
  reg: ServiceWorkerRegistration,
  tag: string,
  ids: NotificationIdentity = {},
): Promise<boolean> {
  if (typeof reg.getNotifications !== 'function') return false;
  try {
    const list = await reg.getNotifications();
    return list.some((n) => {
      if (n.tag && n.tag === tag) return true;
      const d = (n.data || {}) as NotificationIdentity;
      return (
        sameId(ids.message_id, d.message_id) ||
        sameId(ids.notification_id, d.notification_id) ||
        sameId(ids.call_id, d.call_id)
      );
    });
  } catch {
    return false;
  }
}

export interface CollapsingNotification {
  title: string;
  body: string;
  tag: string;
  /** Where a click goes (routed in-app by the worker's notificationclick). */
  url: string;
  data?: Record<string, unknown> & NotificationIdentity;
  requireInteraction?: boolean;
  icon?: string;
  /** Also from a secondary window (pop-out / iframe tab). Default: main window only. */
  anyWindow?: boolean;
}

/**
 * 'shown'     shown through the service worker (collapses with the push)
 * 'duplicate' a push already showed this event here: nothing shown
 * 'unavailable' no registration / permission (or the desktop app): use the old path
 */
export type CollapsingResult = 'shown' | 'duplicate' | 'unavailable';

export async function showCollapsingNotification(n: CollapsingNotification): Promise<CollapsingResult> {
  if (!n.anyWindow && isSecondaryAppWindow()) return 'unavailable';
  const reg = await notificationRegistration();
  if (!reg) return 'unavailable';
  listenForPushShown();
  const ids: NotificationIdentity = n.data || {};
  if (wasShownByPush(notificationKeys(n.tag, ids))) return 'duplicate';
  if (await isOnScreen(reg, n.tag, ids)) return 'duplicate';
  try {
    const options: NotificationOptions & { renotify?: boolean } = {
      body: n.body,
      icon: n.icon || DEFAULT_ICON,
      badge: DEFAULT_BADGE,
      tag: n.tag,
      // The push for the same tag may land after this one: it replaces it
      // silently (the worker drops renotify when the tag is on screen).
      renotify: false,
      requireInteraction: !!n.requireInteraction,
      data: { ...(n.data || {}), url: n.url },
    };
    await reg.showNotification(n.title, options);
    return 'shown';
  } catch {
    return 'unavailable';
  }
}

/** Close this browser's service-worker notifications with `tag` (the push's too). Never throws. */
export async function closeCollapsingNotifications(tag: string): Promise<void> {
  const reg = await notificationRegistration();
  if (!reg || typeof reg.getNotifications !== 'function') return;
  try {
    const list = await reg.getNotifications({ tag });
    list.forEach((n) => {
      try {
        n.close();
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
}

/** The message id a notification link carries (`?message=<id>`), or null. */
export function messageIdFromLink(link: string | null | undefined): number | null {
  if (!link) return null;
  try {
    const raw = new URL(link, 'https://weavein.invalid').searchParams.get('message');
    const id = Number(raw);
    return raw && Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Tests only. */
export function __resetBrowserNotifications(): void {
  shownByPush.clear();
  if (listening && typeof navigator !== 'undefined' && navigator.serviceWorker) {
    try {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    } catch {
      /* ignore */
    }
  }
  listening = false;
}
